import { AppState } from '../state/appState';
import {
	CallFieldMappingDestination,
	CallFieldMappingConfig,
	CallLogData,
	CallModeDirectionCallFieldMappingConfig,
	ChooseLogFilesStep,
	CreateJobState,
	CreateJobStep,
	DateCallFieldMappingConfig,
	DirectionCallFieldMappingConfig,
	FilenameCallFieldMappingConfig,
	GatherInfoStep,
	ParsedCallLogFile,
	ParsedCallLogFileData,
	ParticipantNameCallFieldMappingConfig,
	ParticipantNameMappingFormat,
	SessionIdCallFieldMappingConfig
} from '../state/createJobState';
import { defaultMemoize } from 'reselect'
import { parseDateString } from '../../helpers/dates';
import { CallDirection } from '../../models/dto/callDirection';
import { MappingRequirementType } from '../../models/mappingRequirementType';
import { filterByType, logError, memoize } from '../../helpers/utilities';

const SPACES_REGEX = /\s+/;
const COMMA_REGEX = /,/;

const agentFirstDestination: CallFieldMappingDestination = { type: 'field', field: 'agentFirstName' };
const agentLastDestination: CallFieldMappingDestination = { type: 'field', field: 'agentLastName' };
const customerFirstDestination: CallFieldMappingDestination = { type: 'field', field: 'customerFirstName' };
const customerLastDestination: CallFieldMappingDestination = { type: 'field', field: 'customerLastName' };

export const getCreateJobNameFromAppState = defaultMemoize((state: AppState, engagementId: number): string => {
	const createJobState = getCreateJobState(state, engagementId);
	return !createJobState ? '' : getCreateJobName(createJobState);
});

export const getCreateJobName = defaultMemoize((state: CreateJobState): string => {
	const infoStep = getAllCreateJobSteps(state).find<GatherInfoStep>((s): s is GatherInfoStep => s.type === 'info');
	return infoStep?.name ?? '';
});

export const getAllCreateJobSteps = defaultMemoize((state: CreateJobState): CreateJobStep[] => {
	return [
		...state.prevSteps,
		state.currentStep,
		...state.nextSteps
	];
});

export const isCurrentCreateJobStepValid = defaultMemoize((state: AppState, engagementId: number): boolean => {
	const createJobState = getCreateJobState(state, engagementId);
	if (!createJobState)
		return false;

	return isCreateJobStepValid(createJobState.currentStep, createJobState.callLogData);
});

export const isCreateJobStepValid = defaultMemoize((step: CreateJobStep, callLogData: CallLogData): boolean => {
	switch (step.type) {
		case 'info':
			return !!step.name && !step.customFields.some(f => f.required && !(f.value ?? '').trim());
		case 'logFiles':
			return getCallLogDataResult(step).type === 'valid';
		case 'mapCallFields':
			return getAllCallFieldMappingValidationResults(step.mappings, callLogData).every(v => v.isValid);
	}
});

export const getAllCallFieldMappingValidationResults = defaultMemoize((mappings: CallFieldMappingConfig[], callLogData: CallLogData): CallFieldMappingValidationResult[] => {
	return mappings.map(m => getCallFieldMappingValidationResult(m, callLogData));
});

export function getCallFieldMappingValidationResult(mapping: CallFieldMappingConfig, callLogData: CallLogData): CallFieldMappingValidationResult {
	switch (mapping.type) {
		case 'date':
			return getResultFromValidations(mapping, getDateMappingValidations(mapping, callLogData));
		case 'filename':
			return getResultFromValidations(mapping, getFilenameMappingValidations(mapping, callLogData));
		case 'sessionId':
			return getResultFromValidations(mapping, getSessionIdMappingValidations(mapping, callLogData));
		case 'direction':
			return getResultFromValidations(mapping, getDirectionMappingValidations(mapping, callLogData));
		case 'participantName':
			return getResultFromValidations(mapping, getParticipantNameMappingValidations(mapping, callLogData));
		case 'basic':
			return getResultFromValidations(mapping, getMappingValidationsIncludingDefaults(mapping, callLogData));
	}
}

function getResultFromValidations(mapping: CallFieldMappingConfig, validations: CallFieldMappingValidation[]): CallFieldMappingValidationResult {
	return {
		mapping,
		validations,
		isValid: !validations.some(v => v.type === 'error')
	}
}

export function getCallFieldMappingValidationResultsByMappingId(mappings: CallFieldMappingConfig[], callLogData: CallLogData): Map<string, CallFieldMappingValidationResult> {
	const result: Map<string, CallFieldMappingValidationResult> = new Map();

	getAllCallFieldMappingValidationResults(mappings, callLogData).forEach(v => {
		result.set(v.mapping.id, v);
	});

	return result;
}

export function getErrorCallFieldValidations(result: CallFieldMappingValidationResult, includeNonIntrusive: boolean): ReadonlyArray<CallFieldMappingErrorValidation> {
    return filterByType<CallFieldMappingErrorValidation, CallFieldMappingValidation>(result.validations, v => v.type === 'error' && (!v.nonIntrusive || includeNonIntrusive) ? v : null);
}

export function getWarningCallFieldValidations(result: CallFieldMappingValidationResult): ReadonlyArray<CallFieldMappingWarningValidation> {
    return filterByType<CallFieldMappingWarningValidation, CallFieldMappingValidation>(result.validations, v => v.type === 'warning' ? v : null);
}


// NOTE: setting the cacheSize to 4 - that should be plenty since it's
// very unlikely we'll have more than 4 date fields present - many times there
// will only be 1
const getDateMappingValidations = memoize((mapping: DateCallFieldMappingConfig, data: CallLogData): CallFieldMappingValidation[] => {
	return getMappingValidationsIncludingDefaults(mapping, data, () => {
		const columnIndex = mapping.columnIndex;
		const result: CallFieldMappingValidation[] = [];
		const isRequired = mapping.requirementType === MappingRequirementType.Required;
		
		if (isRequired && columnIndex == null) {
			result.push({ type: 'error', message: 'Required field missing', nonIntrusive: true });
		}
		
		if (columnIndex != null) {
			const invalidRowCount = data.callRows.filter(r => {
				const result = parseDateString(getCallLogRowValue(r, columnIndex), mapping.timezone);
				return result.type === 'invalid' || (result.type === 'none' && isRequired);
			}).length;
			if (invalidRowCount > 0) {
				result.push({
					type: 'error',
					message: invalidRowCount === 1
						? '1 call has an invalid date'
						: `${invalidRowCount.toLocaleString()} calls have invalid dates`
				});
			}
		}
		
		return result;
	});
}, 4);

const getFilenameMappingValidations = defaultMemoize((mapping: FilenameCallFieldMappingConfig, data: CallLogData): CallFieldMappingValidation[] => {
	return getMappingValidationsIncludingDefaults(mapping, data, () => {
		const columnIndex = mapping.columnIndex;
		const result: CallFieldMappingValidation[] = [];
		
		if (columnIndex == null) {
			result.push({ type: 'error', message: 'Required field missing', nonIntrusive: true });
		}
		
		if (columnIndex != null) {
			// check for missing file names
			const missingCount = data.callRows.filter(r => !(getCallLogRowValue(r, columnIndex)).trim()).length;
			if (missingCount > 0) {
				result.push({
					type: 'error',
					message: missingCount === 1
						? '1 call is missing an audio file'
						: `${missingCount.toLocaleString()} calls are missing audio files`
				});
			}

			// check for duplicate file names
			let existing: Set<string> = new Set(),
				duplicateRows: Set<number> = new Set();

			data.callRows.forEach((row, idx) => {
				const filename = (getCallLogRowValue(row, columnIndex)).trim();
				if (existing.has(filename)) {
					duplicateRows.add(idx);
				} else {
					existing.add(filename);
				}
			});

			const duplicateCount = duplicateRows.size;

			if (duplicateCount > 0) {
				result.push({
					type: 'warning',
					message: duplicateCount === 1
						? '1 call has a duplicate file name and will be ignored'
						: `${duplicateCount.toLocaleString()} calls have duplicate file names and will be ignored`,
					callRowsToRemove: duplicateRows
				});
			}
		}
		
		return result;
	});
});

const getSessionIdMappingValidations = defaultMemoize((mapping: SessionIdCallFieldMappingConfig, data: CallLogData): CallFieldMappingValidation[] => {
	return getMappingValidationsIncludingDefaults(mapping, data, () => {
		const columnIndex = mapping.columnIndex;
		const result: CallFieldMappingValidation[] = [];
		
		if (columnIndex != null) {
			// check for duplicate session ids
			let existing: Set<string> = new Set(),
				duplicateRows: Set<number> = new Set();

			data.callRows.forEach((row, idx) => {
				const sessionId = (getCallLogRowValue(row, columnIndex)).trim();
				if (!sessionId)
					return;

				if (existing.has(sessionId)) {
					duplicateRows.add(idx);
				} else {
					existing.add(sessionId);
				}
			});

			const duplicateCount = duplicateRows.size;

			if (duplicateCount > 0) {
				result.push({
					type: 'warning',
					message: duplicateCount === 1
						? '1 call has a duplicate call ID and will be ignored'
						: `${duplicateCount.toLocaleString()} calls have duplicate call IDs and will be ignored`,
					callRowsToRemove: duplicateRows
				});
			}
		}
		
		return result;
	});
});

const getDirectionMappingValidations = defaultMemoize((mapping: DirectionCallFieldMappingConfig, data: CallLogData): CallFieldMappingValidation[] => {
	if (mapping.modeConfig.type === 'job')
		return [];

	return getMappingValidationsIncludingDefaults(mapping, data);
});

const getParticipantNameMappingValidations = memoize((mapping: ParticipantNameCallFieldMappingConfig, data: CallLogData): CallFieldMappingValidation[] => {
	return getMappingValidationsIncludingDefaults(mapping, data, () => {
		const invalidCount = data.callRows.filter(row => !!getCallFieldMappingResult(row, mapping).hadNonCriticalParseFailure ).length;
		if (invalidCount > 0) {
			return [
				{
					type: 'warning',
					message: `${invalidCount.toLocaleString()} call${invalidCount === 1 ? ' has an invalid value' : 's have invalid values'}. The ${mapping.displayName} will be ignored on ${invalidCount === 1 ? 'this call' : 'these calls'}.`
				}
			]
		} else {
			return[];
		}
	});
}, 4);

function getMappingValidationsIncludingDefaults(mapping: CallFieldMappingConfig, callLogData: CallLogData, getCustomValidations?: () => CallFieldMappingValidation[]): CallFieldMappingValidation[] {
	let result: CallFieldMappingValidation[] = getMaxLengthMappingValidations(mapping, callLogData);
	
	getColumnIndexesForMappingConfig(mapping).forEach(i => {
		if (!isMappingColumnIndexInRange(i, callLogData)) {
			// this really shouldn't happen because the 
			// UI tries to prevent this
			result.push({ type: 'error', message: 'Selected column is invalid. Select another column.' });
		}
	});
	
	const validations = getCustomValidations ? getCustomValidations() : [];
	return [...result, ...validations];
}

function getMaxLengthMappingValidations(mapping: CallFieldMappingConfig, callLogData: CallLogData): CallFieldMappingValidation[] {
	switch (mapping.type) {
		case 'date':
		case 'direction':
			return [];
		case 'basic':
		case 'filename':
		case 'sessionId':
			const maxLengthConfig = mapping.maxLengthConfig;
			if (maxLengthConfig) {
				const tooLongCount = callLogData.callRows.filter(row => {
					const result = getCallFieldMappingResult(row, mapping);
					const values = result.type === 'single' ? [result.value] : result.values;
					return values.some(v => v.type === 'value' && (v.value ?? '').length > maxLengthConfig.maxLength);
				}).length;

				if (tooLongCount > 0) {
					const suffix = `Max length is ${maxLengthConfig.maxLength.toLocaleString()} characters.`
					return [
						{
							type: 'error',
							message: tooLongCount === 1
								? `1 call has a value that is too long. ${suffix}`
								: `${tooLongCount.toLocaleString()} calls have values that are too long. ${suffix}`
						}
					];
				}
			}
			
			return [];
		case 'participantName':
			return getParticipantNameMaxLengthErrors(mapping, callLogData);
	}
}

function getParticipantNameMaxLengthErrors(config: ParticipantNameCallFieldMappingConfig, callLogData: CallLogData): CallFieldMappingValidation[] {
	const counts: Map<string, { namePart: 'first' | 'last'; column: number; count: number; }> = new Map();
	
	let hasLogged = false;
	callLogData.callRows.forEach(row => {
		const result = getCallFieldMappingResult(row, config);
		const values = result.type === 'single' ? [result.value] : result.values;
		
		values.forEach(value => {
			if (value.type !== 'value') return;
			
			const metadata = value.metadata;
			if (!metadata || metadata.type !== 'participantName') {
				if (!hasLogged) {
					logError('Error trying to validate participant name length: metadata is missing');
					hasLogged = true;
				}
				
				return;
			}
			
			const max = metadata.namePart === 'first' 
				? config.firstMaxLengthConfig.maxLength 
				: config.lastMaxLengthConfig.maxLength;
			
			if ((value.value ?? '').length > max) {
				const key = `${metadata.column}-${metadata.namePart}`;
				const entry: { namePart: 'first' | 'last'; column: number; count: number; } = counts.get(key) ?? { 
					namePart: metadata.namePart, 
					column: metadata.column, 
					count: 0 
				};
				
				counts.set(key, { ...entry, count: entry.count + 1 });
			}
		});
	});
	
	const result: CallFieldMappingValidation[] = [];

	counts.forEach((value, key) => {
		if (value.count > 0) {
			const count = value.count;
			const part = value.namePart;
			const max = part === 'first' ? config.firstMaxLengthConfig.maxLength : config.lastMaxLengthConfig.maxLength;
			
			result.push({
				type: 'error',
				message: `${count.toLocaleString()} ${count === 1 ? 'call has a' : 'calls have a '} ${part === 'first' ? 'first' : 'last'} name that is too long. Max length is ${max.toLocaleString()} characters`,
				sourceColumnIndex: value.column
			});
		}
	});
	
	return result;
}

function isMappingColumnIndexInRange(columnIndex: number, data: CallLogData): boolean {
	// ensure the column index is in range. this shouldn't
	// ever happen but just in case
	if (columnIndex < 0)
		return false;

	const dataColumnCount = data.callRows[0]?.length ?? 0;
	return columnIndex < dataColumnCount;
}

export interface CallFieldMappingWarningValidation {
	type: 'warning';
 	message: string;
 	callRowsToRemove?: Set<number>;
 	sourceColumnIndex?: number;
}

export interface CallFieldMappingErrorValidation {
	type: 'error';
	message: string;
	nonIntrusive?: boolean;
	sourceColumnIndex?: number;
}

export type CallFieldMappingValidation = CallFieldMappingWarningValidation | CallFieldMappingErrorValidation;

export interface CallFieldMappingValidationResult {
	isValid: boolean;
	mapping: CallFieldMappingConfig;
	validations: CallFieldMappingValidation[];	
}

export function getColumnIndexesForMappingConfig(config: CallFieldMappingConfig): number[] {
	switch (config.type) {
		case 'direction':
			if (config.modeConfig.type === 'job') {
				return [];
			} else {
				return config.modeConfig.columnIndex == null ? [] : [config.modeConfig.columnIndex];
			}
		case 'participantName':
			if (config.modeConfig.type === 'singleField') {
				return [config.modeConfig.columnIndex];
			} else {
				const participantIndexes: number[] = [];
				
				if (config.modeConfig.firstNameColumnIndex != null) {
					participantIndexes.push(config.modeConfig.firstNameColumnIndex);
				}
				
				if (config.modeConfig.lastNameColumnIndex != null) {
					participantIndexes.push(config.modeConfig.lastNameColumnIndex);
				}
				
				return participantIndexes;
			}
		case 'sessionId':
		case 'filename':
		case 'date':
		case 'basic':
			return config.columnIndex == null ? [] : [config.columnIndex];
	}
}

export const getCallLogDataResult = defaultMemoize((step: ChooseLogFilesStep): CallLogDataResult => {
	const files = step.files;

	if (!files.length)
		return { type: 'invalid', reason: { type: 'noFiles' } };
	
	// i guess we'll just ignore files that are empty...
	const fileDatas = files.map(getCallLogFileData).filter(d => {
		return d.rows.length > 1 && !!d.rows[0].length;
	})
	
	if (!fileDatas.length)
		return { type: 'invalid', reason: { type: 'empty' } };
	
	let headerRow = fileDatas[0].rows[0],
		hasColumnCountMismatch = false,
		hasColumnNameMismatch = false;
	
	fileDatas.forEach(f => {
		const headerRow2 = f.rows[0];
		
		if (headerRow2.length !== headerRow.length) {
			hasColumnCountMismatch = true;
		}
		
		if (headerRow2.some((item, idx) => getNormalizedHeaderName(item) !== getNormalizedHeaderName(headerRow[idx]))) {
			hasColumnNameMismatch = true;
		}
	});
	
	if (hasColumnCountMismatch)
		return { type: 'invalid', reason: { type: 'schemaMismatch', description: 'The selected files do not all contain the same number of columns. Please ensure each document contains the same headers or data schema.' } };
	
	if (hasColumnNameMismatch)
		return { type: 'invalid', reason: { type: 'schemaMismatch', description: 'The selected files do not all contain the same headers. Please ensure each document contains the same headers or data schema.' } };

	return {
		type: 'valid',
		data: {
			headers: headerRow,
			callRows: fileDatas.flatMap<string[]>(data => {
				return data.rows.slice(1);
			})
		}
	}
});

function getCallLogFileData(file: ParsedCallLogFile): ParsedCallLogFileData {
	switch (file.type) {
		case 'csv':
			return file.data;
		case 'xls':
			return file.selectedSheet.data;
	}
}

function getNormalizedHeaderName(name: string): string {
	return (name ?? '').trim();
}

export const getCallLogDataValidationError = defaultMemoize((step: ChooseLogFilesStep): string | null => {
	const result = getCallLogDataResult(step);
	if (result.type === 'valid')
		return null;

	switch (result.reason.type) {
		case 'noFiles':
			return null;
		case 'empty':
			return 'The selected files are empty.';
		case 'schemaMismatch':
			return result.reason.description;
	}
});

interface CallLogInvalidDataResult {
    type: 'invalid';
    reason: { type: 'noFiles'; } | { type: 'empty'; } | { type: 'schemaMismatch'; description: string; }
}

type CallLogDataResult = { type: 'valid'; data: CallLogData; } | CallLogInvalidDataResult;

function getCreateJobState(state: AppState, engagementId: number): CreateJobState | undefined {
	return state.createJobsState.byEngagement.get(engagementId);
}

export function getCallFieldMappingResult(row: string[], config: CallFieldMappingConfig): CallFieldMappingResult {
	switch (config.type) {
		case 'date':
			if (config.columnIndex == null)
				return { type: 'single', value: { type: 'none' } };

			const dateResult = parseDateString(getCallLogRowValue(row, config.columnIndex), config.timezone);
			if (dateResult.type === 'valid') {
				return { type: 'single', value: { type: 'value', value: dateResult.json, displayValue: dateResult.display, destination: config.destination } };
			} else if (dateResult.type === 'none') {
				return { type: 'single', value: { type: 'none' } };
			} else {
				return { type: 'single', value: { type: 'invalid' } };
			}
		case 'direction':
			if (config.modeConfig.type === 'job' || config.modeConfig.columnIndex == null) {
				return { type: 'single', value: { type: 'none' } };
			} else {
				return { 
					type: 'single', 
					value: { 
						type: 'value', 
						value: getCallDirectionForValue(getCallLogRowValue(row, config.modeConfig.columnIndex), config.modeConfig),
						destination: config.destination
					}
				};
			}
		case 'participantName':
			return getParticipantCallFieldMappingResult(row, config);
		case 'basic':
		case 'filename':
		case 'sessionId':
			return config.columnIndex == null
				? { type: 'single', value: { type: 'none' } } 
				: { type: 'single', value: { type: 'value', value: getCallLogRowValue(row, config.columnIndex), destination: config.destination } };
	}
}

function getParticipantCallFieldMappingResult(row: string[], config: ParticipantNameCallFieldMappingConfig): CallFieldMappingResult {
	const result: CallFieldMappingResult = { type: 'multiple', values: [] };
	const firstDestination = config.participantType === 'agent' ? agentFirstDestination : customerFirstDestination;
	const lastDestination = config.participantType === 'agent' ? agentLastDestination : customerLastDestination;
	const firstDisplayName = 'First name';
	const lastDisplayName = 'Last name';
	
	if (config.modeConfig.type === 'singleField') {
		 const value = getCallLogRowValue(row, config.modeConfig.columnIndex);
		 const firstMetadata: CallFieldMappingItemValueResultMetadata = { type: 'participantName', column: config.modeConfig.columnIndex, namePart: 'first' };
		 const lastMetadata: CallFieldMappingItemValueResultMetadata = { type: 'participantName', column: config.modeConfig.columnIndex, namePart: 'last' };
		 if (config.modeConfig.format === ParticipantNameMappingFormat.LastCommaFirst) {
		 	const parts = value.split(COMMA_REGEX).map(v => v.trim());
		 	if (parts.length === 2) {
		 		result.values.push({ type: 'value', displayName: firstDisplayName, value: parts[1], destination: firstDestination, metadata: firstMetadata });
		 		result.values.push({ type: 'value', displayName: lastDisplayName, value: parts[0], destination: lastDestination, metadata: lastMetadata });
			} else {
		 		result.hadNonCriticalParseFailure = true;
			}
		 } else {
			 const parts = value.split(SPACES_REGEX).map(v => v.trim());
			 if (parts.length > 0) {
				 result.values.push({ type: 'value', displayName: firstDisplayName, value: parts[0], destination: firstDestination, metadata: firstMetadata });
				 result.values.push({ type: 'value', displayName: lastDisplayName, value: parts.length > 1 ? parts[parts.length - 1] : '', destination: lastDestination, metadata: lastMetadata });
			 }
		 }
	} else {
		if (config.modeConfig.firstNameColumnIndex != null) {
			result.values.push({ 
				type: 'value', 
				displayName: firstDisplayName,
				sourceColumnIndex: config.modeConfig.firstNameColumnIndex,
				value: getCallLogRowValue(row, config.modeConfig.firstNameColumnIndex), 
				destination: firstDestination,
				metadata: { type: 'participantName', column: config.modeConfig.firstNameColumnIndex, namePart: 'first' }
			});
		}
		
		if (config.modeConfig.lastNameColumnIndex != null) {
			result.values.push({ 
				type: 'value', 
				displayName: lastDisplayName,
				sourceColumnIndex: config.modeConfig.lastNameColumnIndex,
				value: getCallLogRowValue(row, config.modeConfig.lastNameColumnIndex), 
				destination: lastDestination,
				metadata: { type: 'participantName', column: config.modeConfig.lastNameColumnIndex, namePart: 'last' }
			});
		}
	}
	
	return result;
}

interface CallFieldMappingItemValueResult {
	type: 'value'; value: string; 
	displayValue?: string; 
	destination: CallFieldMappingDestination; 
	displayName?: string;
	sourceColumnIndex?: number;
	metadata?: CallFieldMappingItemValueResultMetadata;
}

type CallFieldMappingItemValueResultMetadata = { type: 'participantName'; namePart: 'first' | 'last'; column: number; };

interface CallFieldMappingItemInvalidResult {
	type: 'invalid'; 
	displayName?: string;
}

interface CallFieldMappingItemNoneResult {
	type: 'none';
}

interface CallFieldMappingSingleItemResult {
	type: 'single'; 
	value: CallFieldMappingItemResult;
	hadNonCriticalParseFailure?: boolean;
}

interface CallFieldMappingMultipleItemResult {
	type: 'multiple'; 
	values: CallFieldMappingItemResult[];
	hadNonCriticalParseFailure?: boolean;
}

export type CallFieldMappingItemResult = CallFieldMappingItemValueResult | CallFieldMappingItemInvalidResult | CallFieldMappingItemNoneResult;
export type CallFieldMappingResult = CallFieldMappingSingleItemResult | CallFieldMappingMultipleItemResult;

export function getCallLogRowValue(row: string[], columnIndex: number): string {
	return row[columnIndex] ?? '';
}

export function getCallDirectionForValue(value: string, modeConfig: CallModeDirectionCallFieldMappingConfig): CallDirection {
	return modeConfig.mappings.get(value) ?? CallDirection.Unknown;
}